import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import { PageShell, PageHero } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { SOLUTIONS, INDUSTRIES } from "@/lib/site";
import { RESOURCES } from "@/lib/resources";
import { ArrowUpRight, ClipboardList, Compass, HardHat, LineChart } from "lucide-react";

/** How a program moves from brief to handover — shown on the Solutions hub. */
const PROGRAM_STAGES = [
  {
    Icon: Compass,
    title: "Discovery & risk brief",
    body: "Site walk, threat and usage profile, stakeholder interviews and a written brief that names the outcome each system has to deliver.",
  },
  {
    Icon: ClipboardList,
    title: "Design & specification",
    body: "Vendor-neutral drawings, device schedules, cable and containment routes, bandwidth and storage maths, and a tender-ready specification.",
  },
  {
    Icon: HardHat,
    title: "Execution oversight",
    body: "Contractor coordination, staged inspections, integration testing across security, automation and AV, and snag closure before handover.",
  },
  {
    Icon: LineChart,
    title: "Commissioning & operations",
    body: "Acceptance tests against the original brief, SOPs and operator training, plus an annual review cycle as the site and threat picture change.",
  },
] as const;

/** Everything that ships with a practice, regardless of which one you start with. */
const ENGAGEMENT_INCLUDES = [
  "One integrated design intent across security, automation, cinema and AV — no orphan systems",
  "Vendor-neutral specification you can tender to any qualified integrator",
  "Budget ranges before design freeze, so scope decisions happen with numbers on the table",
  "Bandwidth, storage, power and containment calculations documented, not assumed",
  "Acceptance test plan written at design stage and signed off at handover",
  "Operator SOPs and training so the system is still usable two years in",
] as const;

const SOLUTIONS_FAQ = [
  {
    q: "Can I engage one practice on its own?",
    a: "Yes. Most programs start with a single practice — usually CCTV and command centre, perimeter protection or home cinema — and expand once the design intent proves out. The design is still written so the other practices can dock into it later without rework.",
  },
  {
    q: "Do you supply and install the equipment?",
    a: "The work is design, specification and execution oversight. Equipment is procured through qualified integrators against a vendor-neutral specification, which keeps pricing competitive and avoids a brand being chosen before the requirement is clear.",
  },
  {
    q: "When is the right time to bring you in?",
    a: "Before structural and MEP drawings freeze. Conduit routes, ceiling voids, gate civil works, acoustic isolation and riser space are the decisions that are expensive to reverse later; device selection is not.",
  },
  {
    q: "Which regions do you work across?",
    a: "Delhi NCR is home ground, with programs delivered across North India and selected sites in Mumbai, Bengaluru and Hyderabad. Remote design plus staged site visits works well for projects outside NCR.",
  },
] as const;

export const SOLUTIONS_HUB_FAQ = SOLUTIONS_FAQ;


export const Route = createFileRoute("/solutions")({
  component: SolutionsLayout,
  // Layout route — title/description set on the leaf (/solutions/) and dynamic leaves.
  // Canonical & og:url MUST stay on leaves only — TanStack concatenates links without dedup.
});

function SolutionsLayout() {
  return <Outlet />;
}

export function SolutionsIndex() {
  return (
    <PageShell>
      <PageHero
        eyebrow="Solutions"
        title={<>Integrated technology practices for <span className="text-primary">secure, intelligent</span> environments.</>}
        description="Security, automation, AV, home cinema, auditorium technology, electronic security and intelligent building systems — planned as one connected framework, not a stack of disconnected purchases."
      />
      <section className="py-24">
        <div className="container-page grid md:grid-cols-2 gap-6">
          {SOLUTIONS.map((s, i) => (
            <Reveal key={s.slug} delay={i * 0.05}>
              <Link
                to="/solutions/$slug"
                params={{ slug: s.slug }}
                className="group relative block h-full overflow-hidden rounded-2xl border border-border bg-background hover:border-border-strong transition-all"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={s.image}
                    alt={`${s.title} reference visual`}
                    width={1600}
                    height={1000}
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 scrim-bottom" />
                </div>
                <div className="p-8">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <h2 className="text-2xl font-semibold tracking-tight">{s.title}</h2>
                    <ArrowUpRight className="h-5 w-5 text-ink-soft group-hover:text-primary transition-colors" />
                  </div>
                  <p className="text-ink-soft leading-relaxed">{s.short}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* How a program runs */}
      <section id="how-we-work" className="scroll-mt-28 py-24 border-t border-border bg-surface">
        <div className="container-page">
          <div className="max-w-2xl">
            <span className="eyebrow">How a program runs</span>
            <h2 className="mt-4 text-3xl md:text-4xl font-semibold tracking-tight">
              Four stages, one accountable design intent.
            </h2>
            <p className="mt-5 text-ink-soft leading-relaxed">
              Every practice below follows the same sequence. It is what keeps a perimeter, a
              command centre and a private cinema on the same network, budget and handover date.
            </p>
          </div>
          <ol className="mt-12 grid gap-6 md:grid-cols-2">
            {PROGRAM_STAGES.map((stage, i) => (
              <Reveal key={stage.title} delay={i * 0.05}>
                <li className="h-full rounded-2xl border border-border bg-background p-7">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <stage.Icon className="h-5 w-5" strokeWidth={1.6} />
                    </span>
                    <span className="text-xs font-medium text-ink-soft">Stage {i + 1}</span>
                  </div>
                  <h3 className="mt-5 text-lg font-semibold tracking-tight">{stage.title}</h3>
                  <p className="mt-2.5 text-sm text-ink-soft leading-relaxed">{stage.body}</p>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* What's included */}
      <section className="py-24 border-t border-border">
        <div className="container-page grid gap-12 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <span className="eyebrow">What's included</span>
            <h2 className="mt-4 text-3xl md:text-4xl font-semibold tracking-tight">
              The same rigour, whichever practice you start with.
            </h2>
            <p className="mt-5 text-ink-soft leading-relaxed">
              A single-system brief and a full multi-practice program are documented to the same
              standard, so nothing has to be re-drawn when the scope grows.
            </p>
          </div>
          <ul className="grid gap-3 self-center">
            {ENGAGEMENT_INCLUDES.map((item) => (
              <li
                key={item}
                className="flex gap-3 rounded-xl border border-border bg-surface/60 p-5 text-sm text-ink-soft leading-relaxed"
              >
                <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Sector + reference cross-links */}
      <section className="py-24 border-t border-border bg-surface">
        <div className="container-page grid gap-14 lg:grid-cols-2">
          <div>
            <span className="eyebrow">By sector</span>
            <h2 className="mt-4 text-2xl md:text-3xl font-semibold tracking-tight">
              See how these practices land in your environment.
            </h2>
            <div className="mt-8 flex flex-wrap gap-2.5">
              {INDUSTRIES.map((ind) => (
                <Link
                  key={ind.slug}
                  to="/industries/$slug"
                  params={{ slug: ind.slug }}
                  className="rounded-full border border-border bg-background px-4 py-2 text-sm hover:border-primary/50 hover:text-primary transition-colors"
                >
                  {ind.title}
                </Link>
              ))}
            </div>
          </div>
          <div>
            <span className="eyebrow">Design references</span>
            <h2 className="mt-4 text-2xl md:text-3xl font-semibold tracking-tight">
              Benchmarks behind the specifications.
            </h2>
            <ul className="mt-8 grid gap-3">
              {RESOURCES.map((r) => (
                <li key={r.slug}>
                  <Link
                    to="/resources/$slug"
                    params={{ slug: r.slug }}
                    className="group flex items-start justify-between gap-4 rounded-xl border border-border bg-background p-5 hover:border-primary/40 transition-colors"
                  >
                    <span className="text-sm font-medium">{r.title}</span>
                    <ArrowUpRight className="h-4 w-4 shrink-0 text-ink-soft group-hover:text-primary transition-colors" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="scroll-mt-28 py-24 border-t border-border">
        <div className="container-page max-w-3xl">
          <span className="eyebrow">FAQ</span>
          <h2 className="mt-4 text-3xl md:text-4xl font-semibold tracking-tight">
            Before you brief us.
          </h2>
          <dl className="mt-10 grid gap-5">
            {SOLUTIONS_FAQ.map((f) => (
              <div key={f.q} className="rounded-2xl border border-border bg-surface/60 p-6">
                <dt className="text-base font-semibold tracking-tight">{f.q}</dt>
                <dd className="mt-2.5 text-sm text-ink-soft leading-relaxed">{f.a}</dd>
              </div>
            ))}
          </dl>
          <Link
            to="/contact"
            className="mt-10 inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-medium text-primary-foreground hover:opacity-90 transition"
          >
            Let's Talk <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </PageShell>
  );
}

